import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';

export interface ComboboxOption {
  value: string;
  label: string;
  hint?: string;
  icon?: ReactNode;
}

export interface ComboboxProps {
  value: string | null;
  options: ComboboxOption[];
  onChange: (value: string | null) => void;
  placeholder?: string;
  /** the label of the empty choice; omit to forbid clearing */
  emptyLabel?: string;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  size?: 'm' | 's';
  /** called with the typed text when the owner fetches options itself */
  onSearch?: (text: string) => void;
  loading?: boolean;
}

/**
 * A searchable select (spec section 2.2): ↑↓ move, Enter picks, Escape restores the value the field had when it opened
 * (never an empty one), a pick by keyboard leaves the next pointer click alone (Q-D-19).
 */
export function Combobox({ value, options, onChange, placeholder, emptyLabel, disabled, id, size, onSearch, loading, ...rest }: ComboboxProps) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [active, setActive] = useState(0);
  const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    if (!typing) setText(selected?.label ?? '');
  }, [selected, typing]);

  const filtered = useMemo(() => {
    const q = text.trim().toLowerCase();
    const base = onSearch || !typing || q === '' ? options : options.filter((o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q) || o.hint?.toLowerCase().includes(q));
    return emptyLabel ? [{ value: '', label: emptyLabel } as ComboboxOption, ...base] : base;
  }, [options, text, typing, emptyLabel, onSearch]);

  const close = (restore: boolean) => {
    setOpen(false);
    setTyping(false);
    if (restore) setText(selected?.label ?? '');
  };

  const pick = (opt: ComboboxOption) => {
    onChange(opt.value === '' && emptyLabel ? null : opt.value);
    setTyping(false);
    setText(opt.value === '' && emptyLabel ? '' : opt.label);
    setOpen(false);
  };

  return (
    <div className="combobox">
      <input
        ref={inputRef}
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && filtered[active] ? `${listId}-${active}` : undefined}
        aria-label={rest['aria-label']}
        className={`input ${size === 's' ? 'input-s' : ''}`}
        placeholder={placeholder}
        disabled={disabled}
        value={text}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setText(e.target.value);
          setTyping(true);
          setOpen(true);
          setActive(0);
          onSearch?.(e.target.value);
        }}
        onBlur={(e) => {
          // a click on an option arrives as mousedown first and picks; any other blur restores the value
          if (e.relatedTarget && (e.relatedTarget as HTMLElement).closest?.('.combobox-list')) return;
          close(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActive((a) => Math.min(filtered.length - 1, a + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (e.key === 'Enter') {
            if (open && filtered[active]) {
              e.preventDefault();
              pick(filtered[active]!);
            }
          } else if (e.key === 'Escape') {
            if (open) {
              e.preventDefault();
              e.stopPropagation();
              close(true);
            }
          }
        }}
      />
      {open && (
        <ul id={listId} role="listbox" className="combobox-list">
          {loading && <li className="combobox-empty">Searching…</li>}
          {!loading && filtered.length === 0 && <li className="combobox-empty">Nothing matches</li>}
          {filtered.map((opt, i) => (
            <li
              key={opt.value || '__empty'}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={opt.value === (value ?? '')}
              className={`combobox-option ${i === active ? 'active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault(); // keeps the focus so the blur never fires between the pick and the click
                pick(opt);
              }}
              onMouseEnter={() => setActive(i)}
            >
              <span>
                {opt.icon} {opt.label}
              </span>
              {opt.hint && <span className="muted small">{opt.hint}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
